// PackingListPage — /receiving/packing-list. A vendor's slip in, a prefilled
// receiving inbox out.
//
// ── THE ERRAND ───────────────────────────────────────────────────────────────
// Mannie's framing: "scan a packing list and fill in a receive form for a human
// to confirm... I would like it to go to a prefilled drawer that the human
// presses receive on." A carton lands with a slip taped to it and somebody would
// otherwise read eleven quantities off it one at a time.
//
// ── WHY THIS SCREEN DOES NOT RECEIVE ANYTHING ───────────────────────────────
// It ends at "recorded", and the button says so. The boxes still go in through
// the order's receive page (/receiving/order/:poId), with every guard it carries
// intact: the location check, the over-receipt tolerance, the
// 037 accepted/rejected split, the label print.
//
// That is not caution for its own sake. What arrives is a physical fact and the
// slip is a claim about it — the two disagree often enough that this feature only
// pays for itself if the disagreement stays visible. So the numbers here are a
// PREFILL, and the count somebody does at the boxes stays the authority.
//
// ── WHY IT IS NOT PoScanPage OR QuoteDraftPage ──────────────────────────────
// Those two end in a purchase order, so their whole job is getting a document's
// lines into the catalog: they mint items, they need both classification axes,
// they carry a SKU grammar. This screen mints NOTHING. The lines already exist —
// they are our own purchase order lines — and the only question per row is WHICH
// one this slip line satisfies and HOW MANY the shipper says are in the box.
//
// What IS carried over unchanged, because a reviewer must not have to relearn it:
// the amber needs-review treatment (components/review) and the holding-cell
// panel (PoScanDrafts).

import React from 'react';
import { buttonClass } from '../components/ui/Button';
import { Tag } from '../components/ui/Tag';
import { Checkbox } from '../components/ui/Checkbox';
import { Field } from '../components/ui/Field';
import { FileField } from '../components/ui/FileField';
import { Select } from '../components/ui/Select';
import { Tooltip } from '../components/ui/Tooltip';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/ui/EmptyState';
import { LogoSpinner } from '../components/ui/LogoSpinner';
import { Panel } from '../components/ui/Panel';
import { Link, useHistory } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import HelpDrawer from '../components/HelpDrawer';
import { itemLabel } from '../components/ItemName';
import useUserStore from '../stores/useUserStore';
import ScanDraftsPanel, { ScanDraftPages } from '../Procurement/PoScanDrafts';
import { ReviewNote, type ReviewSlot } from '../components/review/ReviewNote';
import { ConfidenceDot } from '../components/review/confidence';
import { laneLabel as labelClass } from '../components/review/fieldStyles';
import { groupSlots } from '../components/review/groupSlots';
import { ConfirmBar } from '../components/review/ConfirmBar';
import { formatCents } from '../Procurement/poApi';
import PageLayout from '../components/ui/PageLayout';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import {
  usePackingListApi,
  reviewKey,
  packingListDraftPageUrl,
  type PackingListParseResponse,
  type PackingListExtraction,
  type PackingListLine,
  type PackingListReviewEntry,
  type PackingListDraftSummary,
  type ScanDraftFile,
} from './packingListApi';

/**
 * This lane's reason tags. A superset of the import lane's, because a slip can
 * disagree with an order in ways no other document can.
 *
 * Every tag names WHAT IS WRONG, not what to do: the entry's own message says
 * that, and a tag that repeats the instruction makes the message look skippable.
 */
const PACKING_LIST_REASON_LABELS: Record<string, string> = {
  low_confidence: "AI wasn't sure",
  unverified_text: 'Not found on the page',
  ungrounded_scan: 'Nothing could be checked',
  ai_remark: 'The reader left a note',
  model_not_sure: "AI wasn't sure",
  vendor_is_self: 'Shipper may be us',
  prices_dropped_currency: 'Prices not in dollars',
  po_number_unknown: 'Order not found here',
  ambiguous_match: 'Could be either order',
  description_match: 'Matched on wording',
  unmatched_line: 'Not on any open order',
  line_already_received: 'That line is closed',
  quantity_over_ordered: 'More than outstanding',
  price_variance: 'Priced differently',
};

/** One row of the review table: a slip line plus what the person has changed. */
interface SlipLineDraft {
  key: string;
  lineNumber: number;
  description: string | null;
  partNumber: string | null;
  quantityShipped: number;
  lotNumber: string;
  unitPriceCentsObserved: number | null;
  purchaseOrderLineId: string | null;
  partCatalogId: number | null;
  skuGuess: string | null;
  matchReason: PackingListLine['match_reason'];
  matchScore?: number;
  candidates: PackingListLine['candidates'];
  matchedRemaining: number | null;
  matchedUnitCostCents: number | null;
  matchedPoNumber: string | null;
  included: boolean;
}

function draftLinesFrom(staged: PackingListParseResponse): SlipLineDraft[] {
  return staged.extraction.lines.map((line) => ({
    key: `l${line.line_number}`,
    lineNumber: line.line_number,
    description: line.description,
    partNumber: line.part_number,
    quantityShipped: line.quantity_shipped,
    lotNumber: line.lot_number || '',
    unitPriceCentsObserved: line.unit_price_cents_observed ?? null,
    purchaseOrderLineId: line.purchase_order_line_id,
    partCatalogId: line.part_catalog_id,
    skuGuess: line.sku_guess,
    matchReason: line.match_reason,
    matchScore: line.match_score,
    candidates: line.candidates || [],
    matchedRemaining: line.matched_remaining ?? null,
    matchedUnitCostCents: line.matched_unit_cost_cents ?? null,
    matchedPoNumber: line.matched_po_number,
    // A line that matched nothing starts SWITCHED OFF. It is still on the screen,
    // still recorded if the reviewer turns it on, but recording an unordered
    // arrival is a decision rather than the default.
    included: line.match_reason !== 'unmatched',
  }));
}

/**
 * What the attached catalog item is CALLED, when this slip line is attached to
 * one. Null when it is not, or when the name adds nothing to the SKU guess
 * already printed beside it.
 */
function matchedItemName(line: SlipLineDraft): string | null {
  if (!line.purchaseOrderLineId) return null;
  const match = line.candidates.find(
    (c) => c.purchase_order_line_id === line.purchaseOrderLineId
  );
  if (!match) return null;
  const label = itemLabel(match);
  return label && label !== line.skuGuess && label !== '—' ? label : null;
}

/** Every open order line this slip could point at, deduped across all rows. */
function allCandidates(lines: SlipLineDraft[]) {
  const map = new Map<string, PackingListLine['candidates'][number]>();
  for (const line of lines) {
    for (const c of line.candidates) {
      if (!map.has(c.purchase_order_line_id)) map.set(c.purchase_order_line_id, c);
    }
  }
  return [...map.values()];
}

export const PackingListPage: React.FC = () => {
  const api = usePackingListApi();
  const toast = useToast();
  const history = useHistory();
  const user = useUserStore((s) => s.user);
  const canSeeFinancials = !!user?.can_financial;

  const [parsed, setParsed] = React.useState<PackingListParseResponse | null>(null);
  const [lines, setLines] = React.useState<SlipLineDraft[]>([]);
  const [reading, setReading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [readError, setReadError] = React.useState<string | null>(null);
  const [slipNumber, setSlipNumber] = React.useState('');
  const [shipDate, setShipDate] = React.useState('');
  /** reviewKey → how the person answered it. Absent means still open. */
  const [resolved, setResolved] = React.useState<Record<string, 'edited' | 'confirmed_as_staged'>>({});

  // ── the holding cell ─────────────────────────────────────────────────────
  // Every parse stages what it read plus the pages it read from, so walking away
  // does not throw away a paid model call and a half-finished review. This matters
  // more here than in the sibling lanes: the reading happens at the boxes, on a
  // phone, and the person who finishes it is often at a desk.
  const [drafts, setDrafts] = React.useState<PackingListDraftSummary[]>([]);
  const [draftId, setDraftId] = React.useState<number | null>(null);
  const [draftFiles, setDraftFiles] = React.useState<ScanDraftFile[]>([]);
  const [draftBusyId, setDraftBusyId] = React.useState<number | null>(null);

  const fileInput = React.useRef<HTMLInputElement | null>(null);

  const extraction: PackingListExtraction | null = parsed ? parsed.extraction : null;

  // ── the needs-review gate ─────────────────────────────────────────────────
  // The kit's grouping, shared with the quote and catalog-import lanes: one amber
  // note per place on the page, one "Looks right" clearing all of it.
  const slots = React.useMemo(
    () =>
      groupSlots(parsed?.review_required || [], {
        // A RING, not a border: the cards it lands on carry no outline to
        // recolor, and a ring adds no layout so a flagged card stays put.
        ringClass: ' ring-2 ring-amber-500/60',
        isResolved: (key) => Boolean(resolved[key]),
        resolve: (key, resolution) => setResolved((r) => ({ ...r, [key]: resolution })),
      }),
    [parsed, resolved]
  );

  const slotFor = (entry: Pick<PackingListReviewEntry, 'target' | 'field' | 'line_number'>): ReviewSlot | null =>
    slots.get(reviewKey(entry)) || null;

  const openSlots = [...slots.values()].filter((s) => s.open);
  const documentSlot = slotFor({ target: 'document', field: null, line_number: null });

  // EVERY SLOT MUST HAVE SOMEWHERE TO BE CLEARED, or the gate deadlocks: an entry
  // with no "Looks right" button blocks the save forever and the screen gives no
  // way out. Field-target entries have no natural home on this layout (the server
  // decides which header fields it is unsure of), so they render as one block. The
  // entry's own label already names the field. Same omission the quote screen hit.
  const fieldSlots = [...slots.values()].filter(
    (slot) => slot.entries.length > 0 && slot.entries[0].target === 'field'
  );

  const includedLines = lines.filter((l) => l.included);
  const blockers: string[] = [];
  if (includedLines.length === 0) blockers.push('Keep at least one line.');
  if (includedLines.some((l) => !Number.isInteger(l.quantityShipped) || l.quantityShipped <= 0)) {
    blockers.push('Every line needs a shipped quantity above zero.');
  }
  if (openSlots.length > 0) {
    blockers.push(
      `${openSlots.length} thing${openSlots.length === 1 ? '' : 's'} to look at: ${openSlots.map((s) => s.label).join(', ')}.`
    );
  }

  const matchedCount = includedLines.filter((l) => l.purchaseOrderLineId).length;

  const refreshDrafts = React.useCallback(async () => {
    try {
      const data = await api.listDrafts();
      setDrafts(data.drafts || []);
    } catch {
      // The list is a convenience on top of a flow that works without it. A failed
      // fetch must not stop somebody dropping a slip in.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  /** Load one staged slip back onto the screen, exactly as parse left it. */
  function hydrate(staged: PackingListParseResponse, id: number | null, files: ScanDraftFile[] = []) {
    setParsed(staged);
    setLines(draftLinesFrom(staged));
    setSlipNumber(staged.extraction.slip_number || '');
    setShipDate(staged.extraction.ship_date || '');
    // Deliberately NOT restored: which flags were cleared is a judgment, possibly
    // made in another session by another person, and it is cheap to re-clear and
    // expensive to get wrong.
    setResolved({});
    setDraftId(id);
    setDraftFiles(files);
  }

  function reset() {
    setParsed(null);
    setLines([]);
    setDraftId(null);
    setDraftFiles([]);
    setSlipNumber('');
    setShipDate('');
    setResolved({});
  }

  async function openDraft(id: number) {
    setDraftBusyId(id);
    setReadError(null);
    try {
      const draft = await api.getDraft(id);
      hydrate(draft.staged_doc, draft.id, draft.files);
    } catch (err: any) {
      setReadError(err?.data?.error?.message || err?.message || 'That packing list could not be opened.');
      refreshDrafts();
    } finally {
      setDraftBusyId(null);
    }
  }

  async function discardDraft(id: number) {
    setDraftBusyId(id);
    try {
      await api.discardDraft(id);
      if (draftId === id) reset();
      await refreshDrafts();
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'That packing list could not be discarded.');
    } finally {
      setDraftBusyId(null);
    }
  }

  async function readSlip(files: FileList | null) {
    if (!files || files.length === 0) return;
    setReading(true);
    setReadError(null);
    try {
      const result = await api.parse(Array.from(files));
      hydrate(result, result.draft_id ?? null, []);
      refreshDrafts();
    } catch (err: any) {
      // The lane's refusals (no purchase order number, an order we do not have)
      // arrive here with a sentence worth reading and a next step in it, so it is
      // shown rather than replaced.
      setReadError(err?.data?.error?.message || err?.message || 'That packing list could not be read.');
      reset();
    } finally {
      setReading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  function patchLine(key: string, patch: Partial<SlipLineDraft>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  /** Point a row at a different order line, carrying that line's numbers with it. */
  function repoint(key: string, purchaseOrderLineId: string) {
    setLines((prev) =>
      prev.map((l) => {
        if (l.key !== key) return l;
        if (!purchaseOrderLineId) {
          return {
            ...l,
            purchaseOrderLineId: null,
            matchedRemaining: null,
            matchedUnitCostCents: null,
            matchedPoNumber: null,
          };
        }
        const pick = allCandidates(prev).find((c) => c.purchase_order_line_id === purchaseOrderLineId);
        return {
          ...l,
          // SWITCH IT ON. An unmatched row starts off, deliberately — recording
          // an unordered arrival is a decision. But picking the order line it
          // belongs to IS that decision, and leaving the row off afterwards meant
          // somebody chose the right line, pressed Record, and nothing was saved.
          // The only signal was the row staying slightly faded.
          included: true,
          purchaseOrderLineId,
          partCatalogId: pick ? pick.part_catalog_id : l.partCatalogId,
          skuGuess: pick ? pick.sku : l.skuGuess,
          matchedRemaining: pick ? pick.remaining : null,
          matchedUnitCostCents: pick ? pick.unit_cost_cents ?? null : null,
          matchedPoNumber: pick ? pick.po_number : null,
        };
      })
    );
  }

  async function record() {
    if (!parsed || !extraction || blockers.length > 0) return;
    setSaving(true);
    try {
      const result = await api.confirm({
        packing_list: {
          slip_number: slipNumber.trim() || null,
          ship_date: shipDate || null,
          vendor_id: parsed.vendor_resolution.vendor_id,
          vendor_observed: extraction.vendor.name,
          source: parsed.source,
          source_filename: parsed.source_filename,
          lines: lines.map((line) => ({
            line_number: line.lineNumber,
            purchase_order_line_id: line.purchaseOrderLineId,
            part_catalog_id: line.partCatalogId,
            part_number: line.partNumber,
            description: line.description,
            quantity_shipped: line.quantityShipped,
            lot_number: line.lotNumber.trim() || null,
            unit_price_cents_observed: line.unitPriceCentsObserved,
            included: line.included,
          })),
        },
        ...(draftId ? { draft_id: draftId } : {}),
        staged_extraction: extraction,
        staged_confidence: parsed.confidence,
        staged_warnings: parsed.warnings,
        // ONLY what was actually answered. The obvious shortcut is to map every
        // entry and default the unanswered ones to 'confirmed_as_staged' — harmless
        // today, because the button is disabled while anything is open, but it makes
        // the server's gate trivially satisfiable by its own client.
        reviews: (parsed.review_required || [])
          .filter((entry) => resolved[reviewKey(entry)])
          .map((entry) => ({
            target: entry.target,
            field: entry.field,
            line_number: entry.line_number,
            resolution: resolved[reviewKey(entry)],
          })),
      });

      // One order: straight to its receive page, where the slip's numbers wait
      // beside each line. Several (or none): the hub, which lists them all.
      const orderIds = result.purchase_order_ids || [];
      const oneOrder = orderIds.length === 1 ? orderIds[0] : null;
      toast.success(
        result.matched_line_count === 0
          ? 'Shipment recorded. None of its lines matched an open order, so there is nothing to receive against yet.'
          : oneOrder
            ? 'Shipment recorded. Count what turned up and receive it here.'
            : `Shipment recorded across ${orderIds.length} orders. Receive each one from the Inbox.`
      );
      history.push(oneOrder ? `/receiving/order/${oneOrder}` : '/receiving');
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'That shipment could not be recorded.');
    } finally {
      setSaving(false);
    }
  }

  const candidateOptions = allCandidates(lines);

  return (
    <PageLayout kind="focus" className="pt-6" header={(
        <header className="space-y-1 mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-on-surface">Read a packing list</h1>
            <HelpDrawer title="About reading a packing list">
              <p>
                A box turns up with a slip taped to it. Photograph it or drop the PDF here, and
                it is read once: which purchase order it belongs to, what the shipper says is
                in the box, and any lot numbers. You check what it read, and the receiving list
                fills itself in.
              </p>
              <p className="text-on-surface font-medium">Nothing is received here</p>
              <p>
                This records what the vendor <span className="text-on-surface">said</span> they
                sent. When the slip names one order, recording it takes you to that
                order&apos;s receive page, with what the slip says offered beside each line.
                When it names several, you land on the Receiving Hub and receive each order
                from the Inbox. Either way you still count: what you count at the boxes wins
                over what the paper claims.
              </p>
              <p className="text-on-surface font-medium">It needs a purchase order</p>
              <p>
                The whole point is checking what turned up against what was ordered, so a slip
                with no purchase order number on it cannot be read. If a delivery genuinely has
                no order behind it, receive it without one from the Receiving Hub, which will
                ask why.
              </p>
              <p className="text-on-surface font-medium">One slip, several orders</p>
              <p>
                Suppliers combine orders into one shipment, and that is handled. Where the same
                item is open on two of the orders named, you are asked which one it belongs to,
                because nothing on the slip says.
              </p>
              <p className="text-on-surface font-medium">Shipped, not ordered</p>
              <p>
                Slips often print both an ordered and a shipped column. Only the shipped number
                matters here. Where the shipper says they sent more than the order still
                expects, you will see a note: that is allowed, and worth knowing about before
                it is on a shelf.
              </p>
              <p className="text-on-surface font-medium">Prices</p>
              <p>
                Most packing lists carry none, which is normal. Where one does, the price is
                kept as a record and compared against the order, but it never changes what your
                stock is valued at. That stays the price on the purchase order.
              </p>
            </HelpDrawer>
          </div>
          <p className="text-sm text-on-surface-variant">
            Check what the shipper says is in the box, and the order&apos;s receive page will
            be waiting with the slip&apos;s numbers beside each line.{' '}
            <Link to="/receiving/shipments" className="text-primary hover:underline">
              Slips already read
            </Link>
            .
          </p>
        </header>
    )}>
    <div className="space-y-5">

      {/* ── upload ─────────────────────────────────────────────────────────── */}
      <Panel label="Upload the packing list" className="space-y-3">
        <label className="block">
          <span className={labelClass}>The packing list</span>
          <FileField
            ref={fileInput}
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            disabled={reading}
            onChange={(e) => readSlip(e.target.files)}
            aria-label="Choose the packing list to read"
          />
          <span className="mt-1 block text-xs text-on-surface-variant leading-snug">
            One PDF, or photograph the pages in order. A slip in a plastic sleeve photographs
            fine; get the purchase order number in frame.
          </span>
        </label>
        <Reveal show={reading}>
          <Alert tone="info" variant="text" icon={<LogoSpinner size="inline" />}>
            Reading the slip…
          </Alert>
        </Reveal>
        <Reveal show={!!readError}>
          <Alert
            tone="warning"
            action={(
              <Link to="/receiving" className="text-sm text-primary underline hover:text-on-surface">
                Go to the Receiving Hub
              </Link>
            )}
          >
            {readError}
          </Alert>
        </Reveal>
      </Panel>

      {/* ── the holding cell ───────────────────────────────────────────────── */}
      <section className="space-y-2">
        <h2 className="text-[11px] uppercase tracking-wider text-on-surface-variant font-semibold">
          Waiting to be checked
        </h2>
        <ScanDraftsPanel
          drafts={drafts}
          busyId={draftBusyId}
          onReview={openDraft}
          onDiscard={discardDraft}
          pageUrl={packingListDraftPageUrl}
          copy={{
            untitled: 'Unchecked packing list',
            empty: 'Nothing is waiting. Slips you photograph at the boxes show up here.',
            discardQuestion: 'Throw this packing list away? The reading and the photos go with it.',
          }}
        />
      </section>

      {/* ── the review ─────────────────────────────────────────────────────── */}
      {parsed && extraction && (
        <FadeIn className="space-y-5">
          <Panel label="Slip details" className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block">
                <span className={labelClass}>Packing slip number</span>
                <Field
                  thumb
                  value={slipNumber}
                  onChange={(e) => setSlipNumber(e.target.value)}
                  placeholder="Not printed"
                />
              </label>
              <label className="block">
                <span className={labelClass}>Ship date</span>
                <Field thumb type="date" value={shipDate} onChange={(e) => setShipDate(e.target.value)} />
              </label>
              <div className="block">
                <span className={labelClass}>Supplier</span>
                <p className="text-sm text-on-surface py-1.5">
                  {parsed.vendor_resolution.vendor_name || extraction.vendor.name || '—'}
                </p>
                {parsed.vendor_resolution.source === 'purchase_order' && (
                  <p className="text-[11px] text-on-surface-variant leading-snug">
                    From the purchase order, which is more reliable than the letterhead.
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* How sure the reader was about this slip as a whole. Every
                  document in this lane is an AI reading, so it always shows.
                  This row is the slip's own summary line: the orders it names
                  are the one document-level fact it prints. */}
              <ConfidenceDot confidence={parsed.confidence} />
              <span className="text-[11px] uppercase tracking-wider text-on-surface-variant">Orders on this slip</span>
              {parsed.purchase_orders.map((po) => (
                <Link
                  key={po.id}
                  to={`/procurement/po/${po.id}`}
                  className="rounded-full bg-surface-container-highest px-2 py-0.5 font-mono text-[11px] text-primary-ink transition-colors duration-150 ease-emphasized hover:bg-primary/15 motion-reduce:transition-none"
                >
                  {po.po_number}
                </Link>
              ))}
              {parsed.unknown_po_numbers.map((n) => (
                <Tooltip key={n} label="Named on the slip, not found here">
                  <Tag tone="warn" mono>{n}</Tag>
                </Tooltip>
              ))}
            </div>

            {draftId !== null && draftFiles.length > 0 && (
              <ScanDraftPages
                draftId={draftId}
                files={draftFiles}
                sourceFilename={parsed.source_filename}
                pageUrl={packingListDraftPageUrl}
                noun="packing list"
              />
            )}

            {documentSlot && <ReviewNote slot={documentSlot} labels={PACKING_LIST_REASON_LABELS} />}
            {fieldSlots.length > 0 && (
              <div className="space-y-1.5">
                {fieldSlots.map((slot) => (
                  <div key={slot.key}>
                    <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">{slot.label}</span>
                    <ReviewNote slot={slot} labels={PACKING_LIST_REASON_LABELS} />
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* ── the lines ─────────────────────────────────────────────────── */}
          <section className="space-y-2">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <h2 className="text-[11px] uppercase tracking-wider text-on-surface-variant font-semibold">
                What the slip says is in the box
              </h2>
              <span className="text-[11px] text-on-surface-variant tabular-nums">
                {matchedCount} of {includedLines.length} matched to an order line
              </span>
            </div>

            <ul className="space-y-2" data-testid="slip-lines">
              {lines.map((line) => {
                const slot = slotFor({ target: 'line', field: null, line_number: line.lineNumber });
                const over =
                  Number.isInteger(line.matchedRemaining)
                  && line.quantityShipped > (line.matchedRemaining as number);
                return (
                  <li
                    key={line.key}
                    className={`space-y-2 rounded-lg px-3 py-3 ${
                      line.included ? 'bg-surface-container-low' : 'bg-surface opacity-60'
                    }${slot?.ring || ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-on-surface truncate">
                          {line.description || <span className="text-on-surface-variant">No description</span>}
                        </p>
                        <p className="text-[11px] text-on-surface-variant font-mono truncate">
                          {/* The vendor's own part number and the SKU the reader
                              guessed from it. BOTH STAY CODES: this line is the
                              evidence trail back to the paper, and the paper has
                              no names on it. The item's name is added beside it
                              only once a real catalog row is attached. */}
                          {line.partNumber || '—'}
                          {line.skuGuess && <span className="text-primary/80"> → {line.skuGuess}</span>}
                          {matchedItemName(line) && (
                            <span className="font-sans text-on-surface-variant"> · {matchedItemName(line)}</span>
                          )}
                        </p>
                      </div>
                      <Checkbox
                        size="sm"
                        label="Record"
                        className="shrink-0"
                        checked={line.included}
                        onChange={(e) => patchLine(line.key, { included: e.target.checked })}
                      />
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <label className="block">
                        <span className={labelClass}>Shipped</span>
                        <Field
                          thumb
                          type="number"
                          min={1}
                          value={line.quantityShipped}
                          onChange={(e) => {
                            patchLine(line.key, { quantityShipped: parseInt(e.target.value, 10) || 0 });
                            slot?.markEdited();
                          }}
                        />
                      </label>
                      <label className="block">
                        <span className={labelClass}>Lot</span>
                        <Field
                          thumb
                          value={line.lotNumber}
                          onChange={(e) => patchLine(line.key, { lotNumber: e.target.value })}
                          placeholder="Not printed"
                        />
                      </label>
                      <label className="block col-span-2">
                        <span className={labelClass}>Against which order line</span>
                        <Select
                          label="Against which order line"
                          value={line.purchaseOrderLineId || ''}
                          onChange={(key) => {
                            repoint(line.key, key);
                            slot?.markEdited();
                          }}
                          options={[
                            { key: '', label: 'Not on an order' },
                            ...candidateOptions.map((c) => ({
                              key: String(c.purchase_order_line_id),
                              label: `${c.po_number || 'no PO'} line ${c.line_number ?? '?'} · ${itemLabel(c)} · ${c.remaining} still expected`,
                            })),
                          ]}
                        />
                      </label>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-[11px]">
                      {line.matchReason === 'exact_part_number' && (
                        <span className="text-on-surface-variant">
                          Matched on the part number
                          {line.matchedPoNumber ? ` to ${line.matchedPoNumber}` : ''}.
                        </span>
                      )}
                      {line.matchReason === 'description' && (
                        <span className="text-on-surface-variant">
                          Matched on wording only
                          {typeof line.matchScore === 'number' ? ` (${Math.round(line.matchScore * 100)}% of the words)` : ''}.
                        </span>
                      )}
                      {over && (
                        <Tag size="sm" tone="warn" className="tabular-nums">
                          {line.quantityShipped - (line.matchedRemaining as number)} more than outstanding
                        </Tag>
                      )}
                      {line.unitPriceCentsObserved !== null && (
                        <span className="text-on-surface-variant tabular-nums">
                          Slip price {formatCents(line.unitPriceCentsObserved)}
                          {canSeeFinancials && Number.isInteger(line.matchedUnitCostCents) && (
                            <>
                              {' '}
                              vs {formatCents(line.matchedUnitCostCents as number)} ordered
                            </>
                          )}
                        </span>
                      )}
                    </div>

                    {slot && <ReviewNote slot={slot} labels={PACKING_LIST_REASON_LABELS} />}
                  </li>
                );
              })}
            </ul>
          </section>

          {/* ── record ─────────────────────────────────────────────────────── */}
          {/* Recording is not receiving, and the explainer says so in this lane's
              words, so it stays here rather than in the shared bar. */}
          <ConfirmBar
            mode="static"
            className="rounded-lg bg-surface-container-low px-4 py-4 space-y-3"
            blockers={blockers}
            blockersTestId="slip-blockers"
            label="Record this shipment"
            busyLabel="Recording…"
            busy={saving}
            disabled={saving || blockers.length > 0}
            onConfirm={record}
            beforeButton={
              <p className="text-xs text-on-surface-variant leading-relaxed">
                Recording this does not receive anything. You count what actually turned up
                afterwards, on the order&apos;s receive page, with these quantities beside
                each line.
              </p>
            }
            secondaryButton={
              <button
                type="button"
                onClick={reset}
                className={buttonClass('tonal', 'md')}
              >
                Start over
              </button>
            }
          />
        </FadeIn>
      )}

      {/* The empty state TEACHES, rather than saying "no data". Somebody arriving
          here for the first time is holding a box and does not know what this
          wants. */}
      {!parsed && !reading && (
        <FadeIn>
        <EmptyState title="Nothing read yet.">
          Photograph the slip on the box, purchase order number in frame. What it reads goes
          onto the Receiving Hub so you press Receive instead of typing quantities. Use{' '}
          <Link to="/receiving" className="text-primary hover:underline">
            the Receiving Hub
          </Link>{' '}
          directly for a delivery with no paperwork.
        </EmptyState>
        </FadeIn>
      )}
    </div>
    </PageLayout>
  );
};

export default PackingListPage;
